import { z } from "zod";
import { isoDateSchema, paginationQuerySchema } from "./common";
import { messageDirectionEnum, messageFailureCodeEnum, messageStatusEnum, } from "./message";
/**
 * Threaded-conversation contracts. Purely additive — nothing here renames or replaces an existing
 * export, and the Sent/Received/Scheduled endpoints are untouched.
 *
 * The unit of a conversation is the `peer`: the counterparty on a message, which is the recipient
 * on an outbound message and the sender on an inbound one. It is materialised on the messages
 * table as a Postgres GENERATED column so it can be indexed, which is what lets the list and
 * thread queries stay fast without any write path having to maintain it.
 *
 * A peer is NOT always a phone number. Carriers, banks and 2FA services send from GSM 03.38
 * alphanumeric sender IDs ("JazzCash"), and short codes are bare digits — so this is a plain
 * bounded string, never e164PhoneSchema.
 */
/** One row of the conversation list: who, the latest message, and how much of it is unread. */
export const conversationSummarySchema = z.object({
    /** The counterparty. A phone number in any format, an alphanumeric sender ID, or a short code. */
    peer: z.string().min(1).max(64),
    /** Resolved from the user's saved contacts by exact phone match; null when nothing matches. */
    contactName: z.string().nullable(),
    lastMessageId: z.string(),
    lastMessageBody: z.string(),
    lastMessageAt: isoDateSchema,
    lastMessageDirection: messageDirectionEnum,
    lastMessageStatus: messageStatusEnum,
    /** Inbound messages in this conversation that have never been marked read. */
    unreadCount: z.number().int().min(0),
});
export const listConversationsQuerySchema = paginationQuerySchema.extend({
    /** Matches the peer identifier or the resolved contact name, case-insensitively. */
    search: z.string().trim().max(120).optional(),
});
/** A single message inside a thread. Deliberately narrower than the full Message row. */
export const conversationMessageSchema = z.object({
    _id: z.string(),
    direction: messageDirectionEnum,
    status: messageStatusEnum,
    body: z.string(),
    segments: z.number().int().min(1),
    failureCode: messageFailureCodeEnum.nullable(),
    failureReason: z.string().nullable(),
    createdAt: isoDateSchema,
    sentAt: isoDateSchema.nullable(),
    deliveredAt: isoDateSchema.nullable(),
    readAt: isoDateSchema.nullable(),
});
export const conversationThreadQuerySchema = paginationQuerySchema;
/** Response of "mark this conversation read", so the caller can update a badge without refetching. */
export const markConversationReadResultSchema = z.object({
    peer: z.string(),
    markedRead: z.number().int().min(0),
    unreadCount: z.number().int().min(0),
});
//# sourceMappingURL=conversation.js.map